// 2026-10-28 · Frescura de la ficha de mesa en el plano del TPV.
//
// Dos trampas que dejaban el badge «cuenta impresa hace X min» mal en el
// dispositivo que actúa:
//
// 1) Lista vieja que pisa un cambio nuevo. Un GET `mesas/restaurante` que se
//    lanzó ANTES de un cambio (evento de tiempo real o parche optimista
//    local) y responde DESPUÉS trae la mesa como era: sin esto, al imprimir y
//    volver al plano la ficha perdía el badge que ya había llegado por el
//    eco. Cada escritura de una mesa avanza un reloj; la respuesta de una
//    lista conserva la mesa del store si se escribió después de lanzar el GET.
//
// 2) Impresión en vuelo. El servidor marca `checkPrintedAt` en segundo plano
//    (tras generar el ticket, ~1–3 s). Mientras tanto puede llegar otra ficha
//    de esa mesa (eco de una acción anterior) aún sin la marca: no debe
//    apagar el badge optimista. La espera termina cuando llega la marca del
//    servidor, cuando la mesa se comanda o se libera, o a los 15 s.
//
// Sin dependencias de Redux para poder testearlo.

let clock = 0;
const writtenAt = new Map(); // mesaId → reloj de la última escritura
const pendingPrint = new Map(); // mesaId → caducidad (ms epoch)

export const PENDING_PRINT_MS = 15000;

const key = (id) => (id == null ? null : String(id).toLowerCase());

/** Una ficha de esta mesa se acaba de escribir en el store (evento u optimista). */
export function noteMesaWrite(mesaId) {
  const k = key(mesaId);
  if (k == null) return;
  clock += 1;
  writtenAt.set(k, clock);
}

/** Marca de inicio de un GET de la lista de mesas. */
export function beginMesasSnapshot() {
  return clock;
}

/**
 * Lista del servidor → lista a guardar: las mesas escritas en el store
 * después de `startedAt` se quedan como están en el store.
 */
export function guardMesasSnapshot(serverMesas, currentMesas, startedAt) {
  if (!Array.isArray(serverMesas) || !Array.isArray(currentMesas) || currentMesas.length === 0) {
    return serverMesas;
  }
  const current = new Map(currentMesas.map((m) => [key(m?.id), m]));
  let changed = false;
  const out = serverMesas.map((m) => {
    const k = key(m?.id);
    const w = writtenAt.get(k);
    if (w != null && w > startedAt && current.has(k)) {
      changed = true;
      return current.get(k);
    }
    return m;
  });
  return changed ? out : serverMesas;
}

/** Este dispositivo acaba de pedir la cuenta impresa de la mesa. */
export function notePendingPrint(mesaId, now = Date.now()) {
  const k = key(mesaId);
  if (k != null) pendingPrint.set(k, now + PENDING_PRINT_MS);
}

/** La mesa se ha comandado / liberado aquí: la cuenta impresa ya no vale. */
export function clearPendingPrint(mesaId) {
  const k = key(mesaId);
  if (k != null) pendingPrint.delete(k);
}

export function hasPendingPrint(mesaId, now = Date.now()) {
  const k = key(mesaId);
  const until = k == null ? undefined : pendingPrint.get(k);
  if (until == null) return false;
  if (now > until) {
    pendingPrint.delete(k);
    return false;
  }
  return true;
}

/**
 * Ficha entrante (evento) frente a la del store: mientras la impresión de
 * ESTE dispositivo está en vuelo, una ficha sin marca no apaga el badge.
 */
export function reconcileIncomingCheckPrinted(existing, incoming, now = Date.now()) {
  if (!incoming) return incoming;
  const id = incoming.id ?? existing?.id;
  if (incoming.ocupada === false) {
    clearPendingPrint(id);
    return incoming;
  }
  if (incoming.checkPrintedAt) {
    clearPendingPrint(id);
    return incoming;
  }
  if (existing?.checkPrintedAt && hasPendingPrint(id, now)) {
    return { ...incoming, checkPrintedAt: existing.checkPrintedAt };
  }
  return incoming;
}

/** Solo tests. */
export function __resetMesaFreshness() {
  clock = 0;
  writtenAt.clear();
  pendingPrint.clear();
}
